import { useEffect, useState } from "react";

declare global {
  interface Window {
    // The connect-residents-app WebView injects a NativeBridge object into the
    // window.
    NativeBridge?: {
      isHeadsetConnected(): boolean;
      // Older native builds may not have this method yet — always optional-call it
      // and treat its absence as "required" (today's behavior).
      isHeadsetRequired?(): boolean;
    };
  }
}

// MDM-controlled per facility (default true) — some facilities (e.g. Elkhart) don't use
// headsets, so headset enforcement must be skippable entirely rather than just reacting to
// "no headset connected".
function isHeadsetRequired(): boolean {
  return window.NativeBridge?.isHeadsetRequired?.() ?? true;
}

/**
 * Audio is normally enabled. It is only disabled when running within the
 * connect-residents-app WebView, headset enforcement is required for this facility, and the
 * user has unplugged their headset — in which case we want to prevent audio from leaking
 * over the speakers.
 */
export default function useMayHearAudio(): boolean {
  // Initialize with the current headset state, if available.
  const [mayHear, setMayHear] = useState<boolean>(() => {
    if (!window.NativeBridge || !isHeadsetRequired()) return true;
    return window.NativeBridge.isHeadsetConnected();
  });

  // Keep in sync with headset plug/unplug events dispatched by the native app.
  useEffect(() => {
    if (!window.NativeBridge || !isHeadsetRequired()) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ connected?: boolean }>).detail;
      if (typeof detail?.connected !== "boolean") return;
      setMayHear(detail.connected);
    };
    window.addEventListener("ameelio:headsetchange", handler);
    return () => window.removeEventListener("ameelio:headsetchange", handler);
  }, []);

  return mayHear;
}
